import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { 
  Plus, Search, Edit, Trash2, Download, LogOut, Users, Award, 
  Clock, CheckCircle, XCircle, ChevronDown, X, Calendar as CalendarIcon
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_a49fb855-3c1f-4bf2-8664-337bfce9e50d/artifacts/sokgx5c0_Gold.png";

// Certification types
const CERTIFICATION_TYPES = [
  "Certified PPF Installation Specialist",
  "Window Tint Installer Specialist",
  "Installation Center Specialist",
  "TC Elite Partner",
  "PPF Protection Kit"
];

// Sidebar Logo component
const SidebarLogo = () => (
  <img 
    src={LOGO_URL} 
    alt="TC PPF" 
    className="h-9 w-auto object-contain"
    style={{ mixBlendMode: 'lighten' }}
  />
);

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { admin, logout, getAuthHeaders } = useAuth();
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCert, setSelectedCert] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    certificate_number: '',
    certification_date: new Date(),
    duration_years: 1,
    certification_type: CERTIFICATION_TYPES[0]
  });
  const [formLoading, setFormLoading] = useState(false);

  useEffect(() => {
    fetchCertificates();
  }, []);

  const fetchCertificates = async () => {
    try {
      const response = await axios.get(`${API}/certificates`, {
        headers: getAuthHeaders()
      });
      setCertificates(response.data);
    } catch (error) {
      console.error('Error fetching certificates:', error);
      toast.error('Failed to load certificates');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      certificate_number: '',
      certification_date: new Date(),
      duration_years: 1,
      certification_type: CERTIFICATION_TYPES[0]
    });
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    
    try {
      const payload = {
        ...formData,
        certification_date: formData.certification_date.toISOString()
      };
      
      await axios.post(`${API}/certificates`, payload, {
        headers: getAuthHeaders()
      });
      
      toast.success('Certificate created successfully');
      setShowAddModal(false);
      resetForm();
      fetchCertificates();
    } catch (error) {
      console.error('Error creating certificate:', error);
      toast.error(error.response?.data?.detail || 'Failed to create certificate');
    } finally {
      setFormLoading(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    
    try {
      const payload = {
        ...formData,
        certification_date: formData.certification_date.toISOString()
      };
      
      await axios.put(`${API}/certificates/${selectedCert.id}`, payload, {
        headers: getAuthHeaders()
      });
      
      toast.success('Certificate updated successfully');
      setShowEditModal(false);
      setSelectedCert(null);
      resetForm();
      fetchCertificates();
    } catch (error) {
      console.error('Error updating certificate:', error);
      toast.error(error.response?.data?.detail || 'Failed to update certificate');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async () => {
    setFormLoading(true);
    
    try {
      await axios.delete(`${API}/certificates/${selectedCert.id}`, {
        headers: getAuthHeaders()
      });
      
      toast.success('Certificate deleted successfully');
      setShowDeleteModal(false);
      setSelectedCert(null);
      fetchCertificates();
    } catch (error) {
      console.error('Error deleting certificate:', error);
      toast.error('Failed to delete certificate');
    } finally {
      setFormLoading(false);
    }
  };

  const openEditModal = (cert) => {
    setSelectedCert(cert);
    setFormData({
      first_name: cert.first_name,
      last_name: cert.last_name,
      certificate_number: cert.certificate_number,
      certification_date: new Date(cert.certification_date),
      duration_years: cert.duration_years,
      certification_type: cert.certification_type || CERTIFICATION_TYPES[0]
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (cert) => {
    setSelectedCert(cert);
    setShowDeleteModal(true);
  };

  const downloadPDF = async (cert) => {
    try {
      const response = await axios.get(`${API}/certificates/${cert.id}/pdf`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `certificate_${cert.certificate_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Certificate downloaded');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download certificate');
    }
  };

  const filteredCertificates = certificates.filter(cert => {
    const matchesSearch = 
      cert.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cert.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cert.certificate_number.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = 
      filterStatus === 'all' ||
      (filterStatus === 'active' && cert.is_active) ||
      (filterStatus === 'expired' && !cert.is_active);
    
    return matchesSearch && matchesFilter;
  });

  const stats = {
    total: certificates.length,
    active: certificates.filter(c => c.is_active).length,
    expired: certificates.filter(c => !c.is_active).length
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-72 bg-[#050505] border-r border-white/10 flex flex-col py-6 px-4 z-50">
        <div className="flex items-center px-2 mb-8">
          <img 
            src={LOGO_URL} 
            alt="TC PPF" 
            className="h-12 w-auto object-contain"
            style={{ mixBlendMode: 'lighten' }}
          />
        </div>

        <nav className="flex-1 space-y-2">
          <Link
            to="/admin/dashboard"
            className="flex items-center gap-3 px-4 py-3 rounded-lg bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/20"
            data-testid="nav-certificates"
          >
            <Award className="w-5 h-5" />
            Certificates
          </Link>
          {admin?.is_super_admin && (
            <Link
              to="/admin/admins"
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:text-white hover:bg-white/5 transition-all"
              data-testid="nav-admins"
            >
              <Users className="w-5 h-5" />
              Admin Management
            </Link>
          )}
        </nav>

        <div className="border-t border-white/10 pt-4 mt-4">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:text-white hover:bg-white/5 transition-all">
                <div className="w-8 h-8 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                  <span className="text-[#D4AF37] text-sm font-bold">{admin?.username?.[0]?.toUpperCase()}</span>
                </div>
                <span className="flex-1 text-left text-sm">{admin?.username}</span>
                <ChevronDown className="w-4 h-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-zinc-900 border-zinc-700">
              <DropdownMenuItem 
                onClick={handleLogout}
                className="text-red-400 hover:text-red-300 hover:bg-red-500/10 cursor-pointer"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-72 p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl text-white mb-2">Certificate Management</h1>
            <p className="text-zinc-400">Manage TC PPF professional certifications</p>
          </div>
          <Button 
            onClick={() => { resetForm(); setShowAddModal(true); }}
            className="btn-gold"
            data-testid="add-certificate-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Certificate
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="glass-card group hover:border-[#D4AF37]/30 transition-colors">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 mb-1">Total</p>
                  <p className="text-3xl font-bold text-white">{stats.total}</p>
                </div>
                <Award className="w-10 h-10 text-[#D4AF37] opacity-50 group-hover:opacity-100 transition-opacity" />
              </div>
            </CardContent>
          </Card>
          <Card className="glass-card group hover:border-emerald-500/30 transition-colors">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 mb-1">Active</p>
                  <p className="text-3xl font-bold text-emerald-500">{stats.active}</p>
                </div>
                <CheckCircle className="w-10 h-10 text-emerald-500 opacity-50 group-hover:opacity-100 transition-opacity" />
              </div>
            </CardContent>
          </Card>
          <Card className="glass-card group hover:border-red-500/30 transition-colors">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 mb-1">Expired</p>
                  <p className="text-3xl font-bold text-red-500">{stats.expired}</p>
                </div>
                <XCircle className="w-10 h-10 text-red-500 opacity-50 group-hover:opacity-100 transition-opacity" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
            <Input
              placeholder="Search by name or certificate number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-11 bg-zinc-900/50 border-zinc-800 text-white placeholder:text-zinc-600"
              data-testid="search-certificates"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-40 bg-zinc-900/50 border-zinc-800 text-white" data-testid="filter-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-700">
              <SelectItem value="all" className="text-white hover:bg-zinc-800">All Status</SelectItem>
              <SelectItem value="active" className="text-white hover:bg-zinc-800">Active</SelectItem>
              <SelectItem value="expired" className="text-white hover:bg-zinc-800">Expired</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Certificates Table */}
        <Card className="glass-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-800">
                  <th className="text-left py-4 px-6 text-xs font-bold tracking-[0.2em] uppercase text-zinc-500">Name</th>
                  <th className="text-left py-4 px-6 text-xs font-bold tracking-[0.2em] uppercase text-zinc-500">Certificate #</th>
                  <th className="text-left py-4 px-6 text-xs font-bold tracking-[0.2em] uppercase text-zinc-500">Issue Date</th>
                  <th className="text-left py-4 px-6 text-xs font-bold tracking-[0.2em] uppercase text-zinc-500">Expires</th>
                  <th className="text-left py-4 px-6 text-xs font-bold tracking-[0.2em] uppercase text-zinc-500">Duration</th>
                  <th className="text-left py-4 px-6 text-xs font-bold tracking-[0.2em] uppercase text-zinc-500">Status</th>
                  <th className="text-right py-4 px-6 text-xs font-bold tracking-[0.2em] uppercase text-zinc-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="py-12 text-center">
                      <div className="animate-spin w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full mx-auto"></div>
                    </td>
                  </tr>
                ) : filteredCertificates.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-12 text-center text-zinc-500">
                      {searchTerm || filterStatus !== 'all' 
                        ? 'No certificates match your search criteria' 
                        : 'No certificates yet. Add your first certificate!'}
                    </td>
                  </tr>
                ) : (
                  filteredCertificates.map((cert) => (
                    <tr 
                      key={cert.id} 
                      className="border-b border-zinc-800/50 table-row-hover transition-colors"
                      data-testid={`certificate-row-${cert.id}`}
                    >
                      <td className="py-4 px-6">
                        <span className="text-white font-medium">{cert.first_name} {cert.last_name}</span>
                      </td>
                      <td className="py-4 px-6">
                        <span className="text-[#D4AF37] font-mono text-sm">{cert.certificate_number}</span>
                      </td>
                      <td className="py-4 px-6 text-zinc-400 text-sm">{formatDate(cert.certification_date)}</td>
                      <td className="py-4 px-6 text-zinc-400 text-sm">{formatDate(cert.expiration_date)}</td>
                      <td className="py-4 px-6 text-zinc-400 text-sm">{cert.duration_years} {cert.duration_years === 1 ? 'Year' : 'Years'}</td>
                      <td className="py-4 px-6">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${
                          cert.is_active ? 'status-active' : 'status-expired'
                        }`}>
                          {cert.is_active ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                          {cert.is_active ? 'Active' : 'Expired'}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => downloadPDF(cert)}
                            className="text-zinc-400 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10"
                            data-testid={`download-${cert.id}`}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditModal(cert)}
                            className="text-zinc-400 hover:text-white hover:bg-white/10"
                            data-testid={`edit-${cert.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDeleteModal(cert)}
                            className="text-zinc-400 hover:text-red-400 hover:bg-red-500/10"
                            data-testid={`delete-${cert.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </main>

      {/* Add Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Add New Certificate</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAdd} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">First Name</Label>
                <Input
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  required
                  className="bg-zinc-900 border-zinc-700 text-white"
                  data-testid="form-first-name"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Last Name</Label>
                <Input
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  required
                  className="bg-zinc-900 border-zinc-700 text-white"
                  data-testid="form-last-name"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-300">Certificate Number</Label>
              <Input
                value={formData.certificate_number}
                onChange={(e) => setFormData({ ...formData, certificate_number: e.target.value })}
                required
                placeholder="e.g., TC-2024-001"
                className="bg-zinc-900 border-zinc-700 text-white"
                data-testid="form-certificate-number"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Certification Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full justify-start text-left font-normal bg-zinc-900 border-zinc-700 text-white hover:bg-zinc-800"
                      data-testid="form-certification-date"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 text-[#D4AF37]" />
                      {formData.certification_date ? format(formData.certification_date, 'PPP') : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-zinc-900 border-zinc-700">
                    <Calendar
                      mode="single"
                      selected={formData.certification_date}
                      onSelect={(date) => date && setFormData({ ...formData, certification_date: date })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Duration (Years)</Label>
                <Select 
                  value={String(formData.duration_years)} 
                  onValueChange={(value) => setFormData({ ...formData, duration_years: parseInt(value) })}
                >
                  <SelectTrigger className="bg-zinc-900 border-zinc-700 text-white" data-testid="form-duration">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700">
                    {[1, 2, 3, 4, 5].map(year => (
                      <SelectItem key={year} value={String(year)} className="text-white hover:bg-zinc-800">
                        {year} {year === 1 ? 'Year' : 'Years'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-300">Certification Type</Label>
              <Select 
                value={formData.certification_type} 
                onValueChange={(value) => setFormData({ ...formData, certification_type: value })}
              >
                <SelectTrigger className="bg-zinc-900 border-zinc-700 text-white" data-testid="form-certification-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-700">
                  {CERTIFICATION_TYPES.map(type => (
                    <SelectItem key={type} value={type} className="text-white hover:bg-zinc-800">
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <DialogFooter className="pt-4">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setShowAddModal(false);
                  resetForm();
                }}
                className="text-zinc-400 hover:text-white"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="btn-gold"
                disabled={formLoading}
                data-testid="form-submit"
              >
                {formLoading ? (
                  <div className="animate-spin w-4 h-4 border-2 border-black border-t-transparent rounded-full"></div>
                ) : 'Create Certificate'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Edit Certificate</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">First Name</Label>
                <Input
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  required
                  className="bg-zinc-900 border-zinc-700 text-white"
                  data-testid="edit-first-name"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Last Name</Label>
                <Input
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  required
                  className="bg-zinc-900 border-zinc-700 text-white"
                  data-testid="edit-last-name"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-300">Certificate Number</Label>
              <Input
                value={formData.certificate_number}
                onChange={(e) => setFormData({ ...formData, certificate_number: e.target.value })}
                required
                placeholder="e.g., TC-2024-001"
                className="bg-zinc-900 border-zinc-700 text-white"
                data-testid="edit-certificate-number"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Certification Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full justify-start text-left font-normal bg-zinc-900 border-zinc-700 text-white hover:bg-zinc-800"
                      data-testid="edit-certification-date"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 text-[#D4AF37]" />
                      {formData.certification_date ? format(formData.certification_date, 'PPP') : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-zinc-900 border-zinc-700">
                    <Calendar
                      mode="single"
                      selected={formData.certification_date}
                      onSelect={(date) => date && setFormData({ ...formData, certification_date: date })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Duration (Years)</Label>
                <Select 
                  value={String(formData.duration_years)} 
                  onValueChange={(value) => setFormData({ ...formData, duration_years: parseInt(value) })}
                >
                  <SelectTrigger className="bg-zinc-900 border-zinc-700 text-white" data-testid="edit-duration">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700">
                    {[1, 2, 3, 4, 5].map(year => (
                      <SelectItem key={year} value={String(year)} className="text-white hover:bg-zinc-800">
                        {year} {year === 1 ? 'Year' : 'Years'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-300">Certification Type</Label>
              <Select 
                value={formData.certification_type} 
                onValueChange={(value) => setFormData({ ...formData, certification_type: value })}
              >
                <SelectTrigger className="bg-zinc-900 border-zinc-700 text-white" data-testid="edit-certification-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-700">
                  {CERTIFICATION_TYPES.map(type => (
                    <SelectItem key={type} value={type} className="text-white hover:bg-zinc-800">
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <DialogFooter className="pt-4">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setShowEditModal(false);
                  resetForm();
                }}
                className="text-zinc-400 hover:text-white"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="btn-gold"
                disabled={formLoading}
                data-testid="edit-submit"
              >
                {formLoading ? (
                  <div className="animate-spin w-4 h-4 border-2 border-black border-t-transparent rounded-full"></div>
                ) : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-center">Delete Certificate</DialogTitle>
          </DialogHeader>
          <div className="py-4 text-center">
            <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
              <Trash2 className="w-8 h-8 text-red-500" />
            </div>
            <p className="text-zinc-300 mb-2">
              Are you sure you want to delete the certificate for
            </p>
            <p className="text-white font-semibold">
              {selectedCert?.first_name} {selectedCert?.last_name}?
            </p>
            <p className="text-zinc-500 text-sm mt-2">This action cannot be undone.</p>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={() => setShowDeleteModal(false)}
              className="text-zinc-400 hover:text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600 text-white"
              disabled={formLoading}
              data-testid="confirm-delete"
            >
              {formLoading ? (
                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
              ) : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
